import { useState, useEffect } from "react";
import { apiFetch } from "../../api/api";
import { T } from "../data/Tokens";
import {
  Card, PageHeader, GoldBtn, GhostBtn, StatusPill,
  SearchBar, Avatar, Th, Td, Tr, Tabs, StatCard, EmptyState, ConfirmModal
} from "../components/common/Ui";

const EMPTY_FORM = { name:"", email:"", phone:"", city:"", status:"New" };
const STATUS_OPTIONS = ["New", "Active", "VIP", "Inactive"];

// ── Input helper ──────────────────────────────────────────────────────────────
const FInput = ({ label, value, onChange, placeholder, type="text", required }) => (
  <div>
    <label className="block text-[11px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color:T.textSec }}>
      {label}{required && <span style={{ color:T.danger }}> *</span>}
    </label>
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none transition-all"
      style={{ background:T.bgSoft, border:`1px solid ${T.border}`, color:T.textPri }}
      onFocus={e=>e.target.style.borderColor=T.gold}
      onBlur={e=>e.target.style.borderColor=T.border}/>
  </div>
);

const FSelect = ({ label, value, onChange, children }) => (
  <div>
    <label className="block text-[11px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color:T.textSec }}>{label}</label>
    <select value={value} onChange={onChange}
      className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none"
      style={{ background:T.bgSoft, border:`1px solid ${T.border}`, color:T.textPri }}>
      {children}
    </select>
  </div>
);

// ── Add/Edit Customer Modal ────────────────────────────────────────────────────
function CustomerFormModal({ customer, onClose, onSave, saving }) {
  const [form, setForm] = useState({ ...EMPTY_FORM, ...(customer || {}) });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.name.trim()) return alert("Name required!");
    if (!form.email.trim()) return alert("Email required!");
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-0 sm:px-4" style={{ background:"rgba(0,0,0,0.5)" }}>
      <div className="w-full h-full sm:h-auto sm:max-w-md rounded-none sm:rounded-3xl overflow-hidden flex flex-col"
        style={{ background:T.card, boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>

        <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0"
          style={{ borderBottom:`1px solid ${T.border}` }}>
          <h2 className="text-[16px] sm:text-[17px] font-bold" style={{ color:T.textPri, fontFamily:T.serif }}>
            {customer?.dbId ? "Edit Customer" : "Add New Customer"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          <FInput label="Full Name" value={form.name} onChange={e=>set("name",e.target.value)} placeholder="Khushi Verma" required />
          <FInput label="Email" value={form.email} onChange={e=>set("email",e.target.value)} placeholder="khushi@gmail.com" type="email" required />
          <FInput label="Phone" value={form.phone} onChange={e=>set("phone",e.target.value)} placeholder="+91 98200 11111" />
          <FInput label="City" value={form.city} onChange={e=>set("city",e.target.value)} placeholder="Mumbai" />
          <FSelect label="Status" value={form.status} onChange={e=>set("status",e.target.value)}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </FSelect>
        </div>

        <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0"
          style={{ borderTop:`1px solid ${T.border}` }}>
          <button onClick={onClose} disabled={saving}
            className="px-4 py-2 rounded-xl text-[13px] font-medium border disabled:opacity-50"
            style={{ borderColor:T.border, color:T.textSec }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-5 py-2 rounded-xl text-[13px] font-semibold text-white disabled:opacity-60"
            style={{ background:T.gold }}>
            {saving ? "Saving..." : (customer?.dbId ? "✓ Update Customer" : "✓ Add Customer")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════════════
export default function CustomersAdmin() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving]       = useState(false);

  const [tab, setTab]       = useState("all");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [delId, setDelId]       = useState(null);

  // ── Fetch customers from backend ────────────────────────────────────────────
  const fetchCustomers = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await apiFetch("/customers/list.php");
      setCustomers(res.data || []);
    } catch (err) {
      setLoadError(err.message || "Failed to load customers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCustomers(); }, []);

  const STATS = [
    { icon:"👥", label:"Total Customers", value: customers.length, sub:"All time", color:T.success },
    { icon:"✨", label:"New",             value: customers.filter(c=>c.status==="New").length,      sub:"Just joined",        color:T.success },
    { icon:"🔄", label:"Active",          value: customers.filter(c=>c.status==="Active").length,   sub:"Ordering regularly", color:"#5B7EE5", bg:"#EEF3FF" },
    { icon:"💎", label:"VIP",             value: customers.filter(c=>c.status==="VIP").length,      sub:"Top spenders",       color:T.gold },
    { icon:"😴", label:"Inactive",        value: customers.filter(c=>c.status==="Inactive").length, sub:"Re-engagement needed", color:T.warning, bg:"#FFF8EC" },
    { icon:"💰", label:"Total Orders",    value: customers.reduce((sum,c)=>sum+(c.orders||0),0),    sub:"Across all customers", color:T.success },
  ];

  const TABS = [
    { id:"all",      label:"All",      count: customers.length },
    { id:"vip",      label:"VIP",      count: customers.filter(c=>c.status==="VIP").length },
    { id:"active",   label:"Active",   count: customers.filter(c=>c.status==="Active").length },
    { id:"new",      label:"New",      count: customers.filter(c=>c.status==="New").length },
    { id:"inactive", label:"Inactive", count: customers.filter(c=>c.status==="Inactive").length },
  ];

  const filtered = customers.filter(c => {
    const ms = !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.email.toLowerCase().includes(search.toLowerCase());
    const mt = tab === "all" || c.status.toLowerCase() === tab;
    return ms && mt;
  });

  // ── CRUD (backend connected) ────────────────────────────────────────────────
  const handleSave = async (formData) => {
    setSaving(true);
    try {
      const { id: _displayId, dbId, ...rest } = formData;
      if (dbId) {
        await apiFetch("/customers/update.php", {
          method: "PUT",
          body: { id: dbId, ...rest },
        });
      } else {
        await apiFetch("/customers/create.php", {
          method: "POST",
          body: rest,
        });
      }
      await fetchCustomers();
      setShowForm(false);
      setEditing(null);
    } catch (err) {
      alert(err.message || "Something went wrong while saving the customer.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (dbId) => {
    try {
      await apiFetch("/customers/delete.php", {
        method: "DELETE",
        body: { id: dbId },
      });
      await fetchCustomers();
    } catch (err) {
      alert(err.message || "Failed to delete customer.");
    } finally {
      setDelId(null);
      setDetail(null);
    }
  };

  const openEdit = (c) => { setEditing(c); setShowForm(true); setDetail(null); };

  return (
    <div>
      <PageHeader title="Customers" subtitle={`${customers.length} total customers`}>
        <GhostBtn>↓ Export CSV</GhostBtn>
        <GoldBtn onClick={() => { setEditing(null); setShowForm(true); }}>+ Add Customer</GoldBtn>
      </PageHeader>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full animate-spin"
              style={{ border: `3px solid ${T.border}`, borderTopColor: T.gold }} />
            <p className="text-[12px]" style={{ color: T.textSec }}>Loading customers...</p>
          </div>
        </div>
      )}

      {!loading && loadError && (
        <Card className="p-6 text-center mb-5">
          <p className="text-[13px] font-semibold mb-2" style={{ color: T.danger }}>{loadError}</p>
          <button onClick={fetchCustomers}
            className="px-4 py-2 rounded-xl text-[12px] font-semibold text-white"
            style={{ background: T.gold }}>
            Retry
          </button>
        </Card>
      )}

      {!loading && !loadError && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-2.5 md:gap-3 mb-4 sm:mb-6">
            {STATS.map(s => <StatCard key={s.label} {...s} />)}
          </div>

          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-1">
            <Tabs tabs={TABS} active={tab} onChange={setTab} />
          </div>

          <Card>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3 px-3.5 sm:px-5 py-3 sm:py-4" style={{ borderBottom:`1px solid ${T.border}` }}>
              <SearchBar value={search} onChange={setSearch} placeholder="Search by name or email..." />
              <div className="flex gap-2">
                <select className="flex-1 sm:flex-none text-[11px] sm:text-[12px] px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-xl outline-none"
                  style={{ border:`1px solid ${T.border}`, background:"#fff", color:T.textSec }}>
                  <option>All Cities</option>
                  {[...new Set(customers.map(c=>c.city).filter(Boolean))].map(city => (
                    <option key={city}>{city}</option>
                  ))}
                </select>
                <select className="flex-1 sm:flex-none text-[11px] sm:text-[12px] px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-xl outline-none"
                  style={{ border:`1px solid ${T.border}`, background:"#fff", color:T.textSec }}>
                  <option>Sort: Newest</option><option>Sort: Most Orders</option><option>Sort: Highest Spend</option>
                </select>
              </div>
            </div>

            {filtered.length === 0
              ? <EmptyState icon="👥" title="No customers found" sub="Try changing search or filter." action="Add Customer" onAction={() => { setEditing(null); setShowForm(true); }} />
              : (
                <>
                  {/* Desktop / tablet table */}
                  <div className="overflow-x-auto hidden sm:block">
                    <table className="w-full min-w-[900px]">
                      <thead>
                        <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                          <Th>Customer</Th><Th>Contact</Th><Th>City</Th>
                          <Th>Joined</Th><Th>Orders</Th><Th>Total Spend</Th>
                          <Th>Last Order</Th><Th>Status</Th><Th>Actions</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((c, i) => (
                          <Tr key={c.dbId} last={i === filtered.length - 1}>
                            <Td>
                              <div className="flex items-center gap-3 cursor-pointer" onClick={() => setDetail(c)}>
                                <Avatar name={c.name} size={34} />
                                <div>
                                  <p className="text-[12px] font-semibold" style={{ color:T.textPri }}>{c.name}</p>
                                  <p className="text-[10px]" style={{ color:T.textSec }}>{c.id}</p>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <p className="text-[11px]" style={{ color:T.textSec }}>{c.email}</p>
                              <p className="text-[11px]" style={{ color:T.textSec }}>{c.phone}</p>
                            </Td>
                            <Td><span className="text-[12px]" style={{ color:T.textSec }}>{c.city || "—"}</span></Td>
                            <Td><span className="text-[11px]" style={{ color:T.textSec }}>{c.joined}</span></Td>
                            <Td><span className="text-[13px] font-bold" style={{ color:T.textPri }}>{c.orders}</span></Td>
                            <Td><span className="text-[13px] font-bold" style={{ color:T.textPri }}>{c.spend}</span></Td>
                            <Td><span className="text-[11px]" style={{ color:T.textSec }}>{c.lastOrder}</span></Td>
                            <Td><StatusPill s={c.status} /></Td>
                            <Td>
                              <div className="flex gap-1">
                                <button onClick={() => setDetail(c)}
                                  className="px-2.5 py-1.5 rounded-lg text-[11px] border hover:bg-amber-50"
                                  style={{ borderColor:T.border, color:T.textSec }}>View</button>
                                <button onClick={() => openEdit(c)}
                                  className="px-2.5 py-1.5 rounded-lg text-[11px] border hover:bg-amber-50"
                                  style={{ borderColor:T.border, color:T.textSec }}>Edit</button>
                                <button onClick={() => setDelId(c.dbId)}
                                  className="px-2.5 py-1.5 rounded-lg text-[11px] border hover:bg-red-50"
                                  style={{ borderColor:T.border, color:T.danger }}>🗑</button>
                              </div>
                            </Td>
                          </Tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile card list */}
                  <div className="sm:hidden divide-y" style={{ borderColor: T.border }}>
                    {filtered.map(c => (
                      <div key={c.dbId} className="p-3.5" onClick={() => setDetail(c)}>
                        <div className="flex items-start gap-3">
                          <Avatar name={c.name} size={38} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-[13px] font-semibold truncate" style={{ color:T.textPri }}>{c.name}</p>
                              <StatusPill s={c.status} />
                            </div>
                            <p className="text-[11px] truncate" style={{ color:T.textSec }}>{c.email}</p>
                            <p className="text-[10.5px]" style={{ color:T.textSec }}>{c.phone}{c.city ? ` · ${c.city}` : ""}</p>
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center gap-3">
                                <span className="text-[11px]" style={{ color:T.textSec }}>{c.orders} orders</span>
                                <span className="text-[12px] font-bold" style={{ color:T.textPri }}>{c.spend}</span>
                              </div>
                              <div className="flex gap-1.5" onClick={e => e.stopPropagation()}>
                                <button onClick={() => openEdit(c)}
                                  className="px-2.5 py-1 rounded-lg text-[10.5px] border"
                                  style={{ borderColor:T.border, color:T.textSec }}>Edit</button>
                                <button onClick={() => setDelId(c.dbId)}
                                  className="px-2.5 py-1 rounded-lg text-[10.5px] border"
                                  style={{ borderColor:T.border, color:T.danger }}>🗑</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
          </Card>
        </>
      )}

      {/* ── Add/Edit Customer Modal ── */}
      {showForm && (
        <CustomerFormModal
          customer={editing}
          saving={saving}
          onClose={() => { if (!saving) { setShowForm(false); setEditing(null); } }}
          onSave={handleSave}
        />
      )}

      {/* ── Customer Detail Panel ── */}
      {detail && (
        <div className="fixed inset-0 z-50 flex" style={{ background:"rgba(0,0,0,0.5)" }} onClick={() => setDetail(null)}>
          <div className="ml-auto w-full max-w-md h-full overflow-y-auto" style={{ background:T.card }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 sticky top-0" style={{ background:T.card, borderBottom:`1px solid ${T.border}` }}>
              <h2 className="text-[16px] sm:text-[17px] font-bold" style={{ color:T.textPri, fontFamily:T.serif }}>Customer Profile</h2>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-700 text-xl">×</button>
            </div>
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
              <div className="flex items-center gap-3 sm:gap-4">
                <Avatar name={detail.name} size={54} />
                <div className="min-w-0">
                  <p className="text-[16px] sm:text-[17px] font-bold truncate" style={{ color:T.textPri }}>{detail.name}</p>
                  <p className="text-[12px] truncate" style={{ color:T.textSec }}>{detail.email}</p>
                  <StatusPill s={detail.status} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 rounded-2xl p-4" style={{ background:T.bgSoft }}>
                {[
                  ["Phone", detail.phone || "—"], ["City", detail.city || "—"],
                  ["Joined", detail.joined], ["Last Order", detail.lastOrder],
                  ["Total Orders", detail.orders], ["Total Spend", detail.spend],
                ].map(([l,v]) => (
                  <div key={l} className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color:T.textSec }}>{l}</p>
                    <p className="text-[13px] font-semibold mt-0.5 truncate" style={{ color:T.textPri }}>{v}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <GoldBtn small onClick={() => openEdit(detail)}>✏️ Edit</GoldBtn>
                <GhostBtn>📦 View Orders</GhostBtn>
                <GhostBtn>🎟 Add Coupon</GhostBtn>
                <button onClick={() => setDelId(detail.dbId)}
                  className="px-3 py-1.5 rounded-xl text-[12px] border"
                  style={{ borderColor:T.border, color:T.danger }}>🗑 Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm ── */}
      {delId && (
        <ConfirmModal
          title="Delete Customer?"
          message={`"${customers.find(c=>c.dbId===delId)?.name}" will be permanently deleted.`}
          onConfirm={() => handleDelete(delId)}
          onCancel={() => setDelId(null)}
        />
      )}
    </div>
  );
}